import React from 'react';

/**
 * HOME-01 — the redesign's inline icons, with the exact path data from
 * Afriqahome_Home_Discover.html.
 *
 * Inline rather than lucide-react because the design draws several of these
 * itself (the m²/bed/bath trio, the amenity glyphs) and no lucide icon matches
 * them. Hand-copying half a set and importing the other half would guarantee
 * two visual languages inside one card, so the whole set is local and the paths
 * are the approved ones.
 */

interface IconProps {
  size?: number;
  className?: string;
  stroke?: string;
  strokeWidth?: number;
}

const Svg: React.FC<IconProps & { children: React.ReactNode; fill?: string }> = ({
  size = 16,
  className = '',
  stroke = 'currentColor',
  strokeWidth = 1.8,
  fill = 'none',
  children,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke={stroke}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

/** Floor area. */
export const AreaIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M3.5 3.5h17v17h-17zM3.5 12h8.5M12 12v8.5" />
  </Svg>
);

/** Bedrooms. */
export const BedIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M2 4v16M2 8h18a2 2 0 0 1 2 2v10M2 17h20M6 8v9" />
  </Svg>
);

/** Bathrooms. */
export const BathIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.7 3 4 3.7 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5H4M10 5 8 7M7 19v2M17 19v2" />
  </Svg>
);

/** Locality pin, on the card's area line. */
export const PinIcon: React.FC<IconProps> = (p) => (
  <Svg strokeWidth={2} {...p}>
    <path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </Svg>
);

export const SearchIcon: React.FC<IconProps> = (p) => (
  <Svg strokeWidth={2} {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Svg>
);

export const ClockIcon: React.FC<IconProps> = (p) => (
  <Svg strokeWidth={2} {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Svg>
);

export const ChevronRightIcon: React.FC<IconProps> = (p) => (
  <Svg strokeWidth={2.5} {...p}>
    <path d="m9 6 6 6-6 6" />
  </Svg>
);

/** In-app messaging, on the hero proof card. */
export const ChatIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.5-.7L3 20.5l1.3-4.5A8.4 8.4 0 1 1 21 11.5z" />
  </Svg>
);

/** Filter sliders — the mobile search row's secondary button. */
export const SlidersIcon: React.FC<IconProps> = (p) => (
  <Svg strokeWidth={2} {...p}>
    <path d="M4 7h16M7 12h10M10 17h4" />
  </Svg>
);

/**
 * The save heart. `saved` drives both fill and stroke, exactly as the design's
 * `heartFill` / `heartStroke` pair does.
 */
/**
 * The save heart.
 *
 * Dirie, on device: "gör hjärtat mycket större och tydligare — nu syns den
 * knappt". Three things were making it disappear, and all three are fixed here
 * rather than by simply scaling it up:
 *
 *   THE SHAPE. The old path was a narrow heart with square joins, which at
 *   15px read as a smudge rather than a symbol. This one is fuller and rounder,
 *   with round caps and joins, so it holds its silhouette at small sizes —
 *   which is the whole job of an icon on a photograph.
 *
 *   THE WEIGHT. 2px of stroke on a 15px glyph is a hairline. 2.25 at a larger
 *   size gives it presence without turning it into a blob.
 *
 *   THE CONTRAST. Near-black (#2D2D2D) rather than the old #3D3D3D, because
 *   this sits over listing photographs that are frequently bright — a Kenyan
 *   sky behind a plot is the worst case and it was the case in the screenshot.
 *
 * Saved is a solid brand red with no outline needed: the filled shape carries
 * it, and the state has to be readable at a glance from across the list.
 */
export const HeartIcon: React.FC<IconProps & { saved?: boolean }> = ({
  size = 16,
  className = '',
  saved = false,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={saved ? '#D64545' : 'none'}
    stroke={saved ? '#D64545' : '#2D2D2D'}
    strokeWidth={2.25}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
  </svg>
);

/**
 * Property-type chip glyphs, in the design's chip order.
 * PLACEHOLDER SET — see HomeHeroSearch. The final taxonomy is an open decision,
 * so these are indexed by position rather than keyed by a type name that may
 * not survive it.
 */
export const TYPE_CHIP_PATHS: readonly string[] = [
  // All types
  'M4 4.5h6.5V11H4zM13.5 4.5H20V11h-6.5zM4 13h6.5v6.5H4zM13.5 13H20v6.5h-6.5z',
  // Apartment
  'M6 21V4.5A1.5 1.5 0 0 1 7.5 3h7A1.5 1.5 0 0 1 16 4.5V21M16 9h2.5A1.5 1.5 0 0 1 20 10.5V21M9.5 7h.01M12.5 7h.01M9.5 11h.01M12.5 11h.01M9.5 15h.01M12.5 15h.01M3 21h18',
  // House / Villa
  'M3 11.5 12 3.5l9 8M5.5 9.5V20.5h13V9.5M10 20.5v-5.5h4v5.5',
  // Townhouse
  'M3 20.5V10l4.5-4.5L12 10v10.5M12 20.5V10l4.5-4.5L21 10v10.5M2 20.5h20M6.5 13.5h1M15.5 13.5h1',
  // Land
  'M2.5 19.5h19M4 19.5 9.5 6.5l4 7 2.5-3.5 4 9.5',
];

export const TypeChipIcon: React.FC<{ index: number; size?: number }> = ({ index, size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="flex-none"
    aria-hidden="true"
    focusable="false"
  >
    <path d={TYPE_CHIP_PATHS[index] ?? TYPE_CHIP_PATHS[0]} />
  </svg>
);
